
import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import {CheckCircle, ArrowRight, Mail, Package, Clock, AlertCircle} from 'lucide-react'
import { lumi } from '../lib/lumi'

const OrderSuccessPage: React.FC = () => {
  console.log('[OrderSuccess] Component mounting...')
  
  const location = useLocation()
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh'
  
  console.log('[OrderSuccess] Current pathname:', location.pathname)
  console.log('[OrderSuccess] Search params:', location.search)
  
  // 從 URL 參數讀取訂單編號和支付狀態
  const searchParams = new URLSearchParams(location.search)
  const orderNumber = searchParams.get('orderNumber')
  const paymentStatus = searchParams.get('payment')
  
  console.log('[OrderSuccess] Parsed orderNumber:', orderNumber)
  console.log('[OrderSuccess] Parsed paymentStatus:', paymentStatus)
  
  const [orderData, setOrderData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // 如果有訂單編號，從資料庫查詢訂單詳情
  useEffect(() => {
    console.log('[OrderSuccess] useEffect triggered')
    
    // 清除藍新金流支付流程标记
    try {
      localStorage.removeItem('newebpay_payment_in_progress')
      console.log('[OrderSuccess] Cleared payment flag')
    } catch (e) {
      console.error('[OrderSuccess] Failed to clear payment flag:', e)
    }
    
    if (orderNumber) {
      console.log('[OrderSuccess] Fetching order:', orderNumber)
      
      const apiUrl = `https://api.lumi.new/v1/functions/p373839333138763776/getOrderByNumber?orderNumber=${encodeURIComponent(orderNumber)}`
      console.log('[OrderSuccess] API URL:', apiUrl)
      
      // 使用 Deno function 查詢訂單（避免前端權限問題）
      fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then(res => {
          console.log('[OrderSuccess] Response status:', res.status)
          return res.json()
        })
        .then(result => {
          console.log('[OrderSuccess] API result:', JSON.stringify(result))
          if (result.success && result.order) {
            console.log('[OrderSuccess] Order found')
            console.log('[OrderSuccess] Order totalAmount:', result.order.totalAmount)
            console.log('[OrderSuccess] Order total:', result.order.total)
            setOrderData({
              orderNumber: result.order.orderNumber,
              total: result.order.totalAmount || result.order.total || 0,
              items: result.order.items?.length || 0
            })
          } else {
            console.warn('[OrderSuccess] Order not found or error:', orderNumber)
            setOrderData({
              orderNumber: orderNumber,
              total: null,
              items: 0
            })
          }
          setLoading(false)
        })
        .catch(err => {
          console.error('[OrderSuccess] API failed:', err.toString())
          setOrderData({
            orderNumber: orderNumber,
            total: null,
            items: 0
          })
          setLoading(false)
        })
    } else if (location.state) {
      console.log('[OrderSuccess] Using location.state:', location.state)
      setOrderData(location.state)
      setLoading(false)
    } else {
      console.log('[OrderSuccess] No order data available')
      setLoading(false)
    }
  }, [orderNumber, location.state])

  const content = {
    zh: {
      title: paymentStatus === 'failed' ? '付款失敗' : '訂單已成功送出！',
      subtitle: paymentStatus === 'failed' ? '很抱歉，您的付款未能成功處理，請重試或選擇其他付款方式' : '感謝您的購買，我們已收到您的訂單',
      orderNumber: '訂單編號',
      total: '訂單金額',
      items: '商品數量',
      nextSteps: {
        title: '接下來會發生什麼？',
        steps: [
          {
            icon: Mail,
            title: '確認信件',
            description: '我們已寄送訂單確認信至您的信箱'
          },
          {
            icon: Package,
            title: '準備出貨',
            description: '我們將在 1-2 個工作天內準備您的商品'
          },
          {
            icon: Clock,
            title: '配送通知',
            description: '商品出貨後將提供追蹤號碼'
          }
        ]
      },
      actions: {
        viewOrders: '查看我的訂單',
        continueShopping: '繼續購物',
        backHome: '返回首頁'
      }
    },
    en: {
      title: paymentStatus === 'failed' ? 'Payment Failed' : 'Order Placed Successfully!',
      subtitle: paymentStatus === 'failed' ? 'We are sorry, your payment could not be processed. Please try again or choose another payment method' : 'Thank you for your purchase, we have received your order',
      orderNumber: 'Order Number',
      total: 'Order Total',
      items: 'Items',
      nextSteps: {
        title: 'What happens next?',
        steps: [
          {
            icon: Mail,
            title: 'Confirmation Email',
            description: 'We have sent an order confirmation to your email'
          },
          {
            icon: Package,
            title: 'Prepare Shipment',
            description: 'We will prepare your items within 1-2 business days'
          },
          {
            icon: Clock,
            title: 'Shipping Notification',
            description: 'Tracking number will be provided once shipped'
          }
        ]
      },
      actions: {
        viewOrders: 'View My Orders',
        continueShopping: 'Continue Shopping',
        backHome: 'Back to Home'
      }
    }
  }

  const t = content[currentLang]

  if (loading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 dark:border-gray-200 mx-auto mb-4"></div>
          <p>載入中...</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 transition-colors duration-300">
      {/* Header Spacer */}
      <div className="pt-14"></div>
      
      {/* Success Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Success/Failed Icon */}
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8 ${
              paymentStatus === 'failed' 
                ? 'bg-red-100 dark:bg-red-900' 
                : 'bg-green-100 dark:bg-green-900'
            }`}>
              {paymentStatus === 'failed' ? (
                <AlertCircle size={48} className="text-red-600 dark:text-red-400" />
              ) : (
                <CheckCircle size={48} className="text-green-600 dark:text-green-400" />
              )}
            </div>
            
            {/* Success Message */}
            <h1 className="text-3xl md:text-4xl font-light tracking-wider text-gray-800 dark:text-gray-200 mb-4">
              {t.title}
            </h1>
            
            <p className="text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              {t.subtitle}
            </p>

            {/* Order Details */}
            {orderData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-8 mb-12 transition-colors duration-300"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                  <div>
                    <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {t.orderNumber}
                    </h3>
                    <p className="text-xl font-light text-gray-800 dark:text-gray-200 tracking-wider">
                      {orderData.orderNumber}
                    </p>
                  </div>
                  
                  {(orderData.total !== null && orderData.total !== undefined) && (
                    <div>
                      <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {t.total}
                      </h3>
                      <p className="text-xl font-light text-gray-800 dark:text-gray-200">
                        NT$ {(orderData.total || 0).toLocaleString()}
                      </p>
                    </div>
                  )}
                  
                  {orderData.items > 0 && (
                    <div>
                      <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {t.items}
                      </h3>
                      <p className="text-xl font-light text-gray-800 dark:text-gray-200">
                        {orderData.items} {currentLang === 'zh' ? '件' : 'items'}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Next Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-light text-gray-800 dark:text-gray-200 text-center mb-8 tracking-wide">
              {t.nextSteps.title}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {t.nextSteps.steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1, duration: 0.6 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <step.icon size={24} className="text-gray-600 dark:text-gray-400" />
                  </div>
                  
                  <h3 className="text-lg font-light text-gray-800 dark:text-gray-200 mb-2 tracking-wide">
                    {step.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to={`/${currentLang}/member/orders`}
              className="inline-flex items-center px-8 py-3 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 hover:bg-gray-700 dark:hover:bg-gray-300 transition-colors duration-200 justify-center"
            >
              {t.actions.viewOrders}
              <ArrowRight size={16} className="ml-2" />
            </Link>
            
            <Link
              to={`/${currentLang}/shop`}
              className="inline-flex items-center px-6 py-3 border border-gray-400 dark:border-gray-500 text-gray-800 dark:text-gray-200 hover:bg-gray-800 hover:text-white dark:hover:bg-gray-200 dark:hover:text-gray-800 transition-colors duration-200 justify-center"
            >
              {t.actions.continueShopping}
            </Link>
            
            <Link
              to={`/${currentLang}`}
              className="inline-flex items-center px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200 justify-center"
            >
              {t.actions.backHome}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default OrderSuccessPage
